import { useQuery, UseQueryOptions } from '@tanstack/react-query';
import { useMemo } from 'react';
import {
  getKidData,
  getKidOverviewData,
  getKidOverviewHistoricalData,
  getKidCostVariables,
  getKidCompareData,
  getKidMarkerAll,
  getJurisdictions,
  getLanguages,
  getLatestKidFigures,
  KidCostVariables,
  getAllKidLayoutStyles,
  KidLayoutStyles,
  readKidLayoutTemplate,
  ReadKidLayoutTemplateParams,
} from '../kids.routes';
import { AxiosError } from 'axios';
import {
  KidOverviewDataItem,
  KidMarker,
  KidFiguresRecordModel,
  KidLayoutTemplateModel,
  KidLayoutStyleSummary,
} from '../types';
import {
  FiguresData,
  injectLatestFiguresIntoElements,
} from '../../../components/pages/raidr/kidsV2/utils/kidDataTransformers';

export const useGetKidData = (
  clientId: string,
  fundId: string,
  shareClass: string,
  documentLanguage: string,
  version?: string,
) => {
  return useQuery({
    queryKey: [
      'kid-data',
      clientId,
      fundId,
      shareClass,
      documentLanguage,
      version,
    ],
    queryFn: () =>
      getKidData(clientId, fundId, shareClass, documentLanguage, version),
    enabled: !!clientId && !!fundId && !!shareClass && !!documentLanguage,
  });
};

export const useGetKidDataWithFigures = (
  clientId: string,
  fundId: string,
  shareClass: string,
  documentLanguage: string,
  version?: string,
) => {
  const kidDataQuery = useGetKidData(
    clientId,
    fundId,
    shareClass,
    documentLanguage,
    version,
  );

  const {
    data: latestFigures,
    isLoading: isLatestFiguresLoading,
    isFetching: isLatestFiguresFetching,
    error: latestFiguresError,
    isError: isLatestFiguresError,
  } = useGetLatestKidFigures(clientId, fundId, shareClass);

  const injectedData = useMemo(
    () =>
      kidDataQuery.data
        ? injectLatestFiguresIntoElements(
            kidDataQuery.data as any[],
            latestFigures?.[0]?.data as FiguresData,
          )
        : kidDataQuery.data,
    [kidDataQuery.data, latestFigures?.[0]?.data],
  );

  return {
    ...kidDataQuery,
    data: injectedData,
    latestKidFigures: latestFigures,
    isLoading: kidDataQuery.isLoading || isLatestFiguresLoading,
    isFetching: kidDataQuery.isFetching || isLatestFiguresFetching,
    // For newer React Query versions where isPending exists:
    isPending:
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (kidDataQuery as any).isPending ||
      false ||
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (latestFigures as any)?.isPending ||
      false,
    error: kidDataQuery.error ?? latestFiguresError,
    isError: kidDataQuery.isError || isLatestFiguresError,
  };
};

// Public hook – uses useQuery for data fetching
export function useGetKidOverviewData(
  clientId: string,
  fundId?: string,
  shareClassId?: string,
  documentLanguage?: string,
  options?: Omit<
    UseQueryOptions<KidOverviewDataItem[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidOverviewDataItem[], AxiosError<any>>({
    queryKey: [
      'kid-overview-data',
      clientId,
      fundId,
      shareClassId,
      documentLanguage,
    ],
    queryFn: () =>
      getKidOverviewData(clientId, fundId, shareClassId, documentLanguage),
    enabled: !!clientId,
    ...options,
  });
}

// Hook for fetching historical KID overview data
export function useGetKidOverviewHistoricalData(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  documentLanguage?: string,
  options?: Omit<
    UseQueryOptions<Record<string, KidOverviewDataItem[]>, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<Record<string, KidOverviewDataItem[]>, AxiosError<any>>({
    queryKey: [
      'kid-historical-overview',
      clientId,
      fundId,
      shareClassId,
      documentLanguage,
    ],
    queryFn: () =>
      getKidOverviewHistoricalData(
        clientId!,
        fundId,
        shareClassId,
        documentLanguage,
      ),
    staleTime: 1000 * 60 * 60 * 24, // 24 hours
    enabled: !!clientId,
    ...options,
  });
}

export function useGetKidCostVariables(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  options?: Omit<
    UseQueryOptions<KidCostVariables, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidCostVariables, AxiosError<any>>({
    queryKey: ['kid-cost-variables', clientId, fundId, shareClassId],
    queryFn: () => getKidCostVariables(clientId!, fundId!, shareClassId!),
    enabled: !!clientId && !!fundId && !!shareClassId,
    ...options,
  });
}

// Hook for fetching KID comparison data
export function useGetKidCompareData(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  comparisonVersion?: string,
  targetVersion?: string,
  documentLanguage?: string,
  options?: Omit<
    UseQueryOptions<
      { comparison_version: any[]; target_version: any[] },
      AxiosError<any>
    >,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<
    { comparison_version: any[]; target_version: any[] },
    AxiosError<any>
  >({
    queryKey: [
      'kid-compare-data',
      clientId,
      fundId,
      shareClassId,
      comparisonVersion,
      targetVersion,
      documentLanguage,
    ],
    queryFn: () =>
      getKidCompareData(
        clientId!,
        fundId!,
        shareClassId!,
        comparisonVersion!,
        targetVersion!,
        documentLanguage || 'English',
      ),
    enabled:
      !!clientId &&
      !!fundId &&
      !!shareClassId &&
      !!comparisonVersion &&
      !!targetVersion,
    ...options,
  });
}

// Hook for fetching all KID markers
export function useGetKidMarkerAll(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  kidFigureCalculationDate?: string,
  overviewLastUpdatingDatetime?: string,
  withValue?: boolean,
  options?: Omit<
    UseQueryOptions<KidMarker[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidMarker[], AxiosError<any>>({
    queryKey: [
      'kid-marker-all',
      clientId,
      fundId,
      shareClassId,
      kidFigureCalculationDate,
      overviewLastUpdatingDatetime,
      withValue,
    ],
    queryFn: () =>
      getKidMarkerAll(
        clientId!,
        fundId!,
        shareClassId!,
        kidFigureCalculationDate,
        overviewLastUpdatingDatetime,
        withValue,
      ),
    enabled: !!clientId && !!fundId && !!shareClassId,
    ...options,
  });
}

// Hook for fetching all jurisdictions
export function useKidJurisdictions(
  options?: Omit<
    UseQueryOptions<string[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<string[], AxiosError<any>>({
    queryKey: ['kid-jurisdictions'],
    queryFn: () => getJurisdictions(),
    ...options,
  });
}

// Hook for fetching all languages
export function useKidLanguages(
  options?: Omit<
    UseQueryOptions<string[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<string[], AxiosError<any>>({
    queryKey: ['kid-languages'],
    queryFn: () => getLanguages(),
    ...options,
  });
}

export function useGetLatestKidFigures(
  clientId?: string,
  fundId?: string,
  shareClassId?: string,
  options?: Omit<
    UseQueryOptions<KidFiguresRecordModel[] | null, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidFiguresRecordModel[] | null, AxiosError<any>>({
    queryKey: ['kid-latest-figures', clientId, fundId, shareClassId],
    queryFn: () => getLatestKidFigures(clientId!, fundId!, shareClassId),
    enabled: !!clientId && !!fundId,
    ...options,
  });
}

export function useGetAllKidLayoutStyles(
  clientId: string,
  options?: Omit<
    UseQueryOptions<KidLayoutStyles, AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidLayoutStyles, AxiosError<any>>({
    queryKey: ['kid-layout-styles', clientId],
    queryFn: () => getAllKidLayoutStyles(clientId),
    enabled: !!clientId,
    ...options,
  });
}

export function useGetKidLayoutTemplate(
  params?: ReadKidLayoutTemplateParams,
  options?: Omit<
    UseQueryOptions<KidLayoutTemplateModel[], AxiosError<any>>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery<KidLayoutTemplateModel[], AxiosError<any>>({
    queryKey: [
      'kid-layout-template',
      params?.kidLayoutId,
      params?.kidLayoutLastUpdatingTimestamp,
      params?.fundId,
      params?.shareClassId,
      params?.kidFigureCalculationDate,
    ],
    queryFn: () => readKidLayoutTemplate(params!),
    enabled:
      options?.enabled !== undefined
        ? options.enabled
        : !!params?.kidLayoutId && !!params?.kidLayoutLastUpdatingTimestamp,
    ...options,
  });
}
